import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { globeDestinations } from '@/mocks/travel';
import { createEarthCanvasTexture } from '@/mocks/continents';

const ORIGIN_LAT = -3.7319;
const ORIGIN_LON = -38.5267;
const RADIUS = 2;

interface DestTooltip {
  name: string;
  country: string;
  region: string;
  emoji: string;
  x: number;
  y: number;
}

function latLonToVec3(lat: number, lon: number, r: number): THREE.Vector3 {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta),
  );
}

function slerpV3(a: THREE.Vector3, b: THREE.Vector3, t: number): THREE.Vector3 {
  const na = a.clone().normalize();
  const nb = b.clone().normalize();
  const dot = Math.max(-1, Math.min(1, na.dot(nb)));
  const omega = Math.acos(dot);
  if (Math.abs(omega) < 0.0001) return na.clone().lerp(nb, t).normalize();
  const s = Math.sin(omega);
  const result = new THREE.Vector3();
  result.addScaledVector(na, Math.sin((1 - t) * omega) / s);
  result.addScaledVector(nb, Math.sin(t * omega) / s);
  return result;
}

function buildArc(
  sLat: number, sLon: number, eLat: number, eLon: number, r: number, n = 80
): THREE.Vector3[] {
  const s = latLonToVec3(sLat, sLon, 1).normalize();
  const e = latLonToVec3(eLat, eLon, 1).normalize();
  return Array.from({ length: n + 1 }, (_, i) => {
    const t = i / n;
    const pt = slerpV3(s, e, t);
    return pt.clone().multiplyScalar(r * (1 + Math.sin(Math.PI * t) * 0.30));
  });
}

export default function WorldMapSection() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tooltip, setTooltip] = useState<DestTooltip | null>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    let animId = 0;
    let renderer: THREE.WebGLRenderer | null = null;
    let camera: THREE.PerspectiveCamera | null = null;
    let earthTex: THREE.CanvasTexture | null = null;
    let eventsAttached = false;

    // Event handler refs for cleanup
    let mmDown: ((e: MouseEvent) => void) | null = null;
    let mmMove: ((e: MouseEvent) => void) | null = null;
    let onUp: (() => void) | null = null;
    let mtStart: ((e: TouchEvent) => void) | null = null;
    let mtEnd: (() => void) | null = null;
    let mtMove: ((e: TouchEvent) => void) | null = null;

    const initScene = (W: number, H: number) => {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(W, H);

      const scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(42, W / H, 0.1, 1000);
      camera.position.z = 5.8;

      // Lights
      scene.add(new THREE.AmbientLight(0x0a1a2e, 0.6));
      const sun = new THREE.DirectionalLight(0x4dd9f0, 0.9);
      sun.position.set(6, 3, 5);
      scene.add(sun);
      const backLight = new THREE.DirectionalLight(0x001a30, 0.4);
      backLight.position.set(-6, -2, -4);
      scene.add(backLight);

      // Stars
      const starPos = new Float32Array(6000);
      for (let i = 0; i < 2000; i++) {
        starPos[i * 3] = (Math.random() - 0.5) * 300;
        starPos[i * 3 + 1] = (Math.random() - 0.5) * 300;
        starPos[i * 3 + 2] = (Math.random() - 0.5) * 300;
      }
      const starsGeo = new THREE.BufferGeometry();
      starsGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
      scene.add(new THREE.Points(starsGeo, new THREE.PointsMaterial({
        color: 0xffffff, size: 0.14, transparent: true, opacity: 0.72,
      })));

      // Globe
      const globeGroup = new THREE.Group();
      scene.add(globeGroup);

      earthTex = createEarthCanvasTexture();
      globeGroup.add(new THREE.Mesh(
        new THREE.SphereGeometry(RADIUS, 72, 72),
        new THREE.MeshPhongMaterial({ map: earthTex, shininess: 18, specular: new THREE.Color(0x112244) })
      ));
      globeGroup.add(new THREE.Mesh(
        new THREE.SphereGeometry(RADIUS * 1.06, 64, 64),
        new THREE.MeshPhongMaterial({ color: 0x00e5ff, side: THREE.BackSide, transparent: true, opacity: 0.09 })
      ));
      globeGroup.add(new THREE.Mesh(
        new THREE.SphereGeometry(RADIUS * 1.02, 64, 64),
        new THREE.MeshPhongMaterial({ color: 0x4dd9f0, side: THREE.BackSide, transparent: true, opacity: 0.04 })
      ));

      // Origin pin
      const originPin = new THREE.Mesh(
        new THREE.SphereGeometry(0.048, 10, 10),
        new THREE.MeshBasicMaterial({ color: 0xff4455 })
      );
      originPin.position.copy(latLonToVec3(ORIGIN_LAT, ORIGIN_LON, RADIUS + 0.06));
      globeGroup.add(originPin);

      // Pulsing ring
      const ringMat = new THREE.MeshBasicMaterial({ color: 0xff4455, side: THREE.DoubleSide, transparent: true, opacity: 0.5 });
      const ring = new THREE.Mesh(new THREE.RingGeometry(0.06, 0.09, 24), ringMat);
      ring.position.copy(latLonToVec3(ORIGIN_LAT, ORIGIN_LON, RADIUS + 0.07));
      ring.lookAt(new THREE.Vector3(0, 0, 0));
      ring.rotateY(Math.PI);
      globeGroup.add(ring);

      // Destination pins + arcs
      const destPins: THREE.Mesh[] = [];
      const arcData: { pts: THREE.Vector3[]; progress: number; particle: THREE.Mesh; lineMat: THREE.LineBasicMaterial }[] = [];

      globeDestinations.forEach((dest, idx) => {
        const pin = new THREE.Mesh(
          new THREE.SphereGeometry(0.040, 10, 10),
          new THREE.MeshBasicMaterial({ color: 0x00e5ff })
        );
        pin.position.copy(latLonToVec3(dest.lat, dest.lon, RADIUS + 0.07));
        pin.userData = { ...dest };
        globeGroup.add(pin);
        destPins.push(pin);

        const pts = buildArc(ORIGIN_LAT, ORIGIN_LON, dest.lat, dest.lon, RADIUS + 0.05);
        const lineMat = new THREE.LineBasicMaterial({ color: 0x4dd9f0, transparent: true, opacity: 0.25 });
        globeGroup.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), lineMat));

        const particle = new THREE.Mesh(
          new THREE.SphereGeometry(0.030, 8, 8),
          new THREE.MeshBasicMaterial({ color: 0xaaf5ff, transparent: true, opacity: 0.95 })
        );
        globeGroup.add(particle);
        arcData.push({ pts, progress: (idx * 0.09) % 1, particle, lineMat });
      });

      // Interaction
      const raycaster = new THREE.Raycaster();
      let drag = false;
      let prevX = 0, prevY = 0;
      let velX = 0, velY = 0;
      const AUTO = 0.0007;
      const DK = 0.007;
      const DAMP = 0.90;

      const handleDown = (x: number, y: number) => { drag = true; prevX = x; prevY = y; velX = 0; velY = 0; };
      const handleUp = () => { drag = false; };
      const handleMove = (x: number, y: number, cx: number, cy: number) => {
        if (drag) {
          const dx = x - prevX;
          const dy = y - prevY;
          velY = dx * DK; velX = dy * DK;
          globeGroup.rotation.y += dx * DK;
          globeGroup.rotation.x = Math.max(-1.1, Math.min(1.1, globeGroup.rotation.x + dy * DK));
          prevX = x; prevY = y;
        }
        if (!camera) return;
        const rect = canvas.getBoundingClientRect();
        const nx = ((cx - rect.left) / rect.width) * 2 - 1;
        const ny = -((cy - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(new THREE.Vector2(nx, ny), camera);
        const hits = raycaster.intersectObjects(destPins);
        if (hits.length > 0) {
          const d = hits[0].object.userData;
          setTooltip({ ...d, x: cx, y: cy });
          canvas.style.cursor = 'pointer';
        } else {
          setTooltip(null);
          canvas.style.cursor = drag ? 'grabbing' : 'grab';
        }
      };

      mmDown = (e: MouseEvent) => handleDown(e.clientX, e.clientY);
      mmMove = (e: MouseEvent) => handleMove(e.clientX, e.clientY, e.clientX, e.clientY);
      onUp = () => handleUp();
      mtStart = (e: TouchEvent) => handleDown(e.touches[0].clientX, e.touches[0].clientY);
      mtEnd = () => handleUp();
      mtMove = (e: TouchEvent) => {
        if (e.touches.length === 1) {
          handleMove(e.touches[0].clientX, e.touches[0].clientY, e.touches[0].clientX, e.touches[0].clientY);
          e.preventDefault();
        }
      };

      canvas.addEventListener('mousedown', mmDown);
      window.addEventListener('mouseup', onUp);
      canvas.addEventListener('mousemove', mmMove);
      canvas.addEventListener('touchstart', mtStart, { passive: true });
      window.addEventListener('touchend', mtEnd);
      canvas.addEventListener('touchmove', mtMove, { passive: false });
      canvas.style.cursor = 'grab';
      eventsAttached = true;

      const animate = () => {
        animId = requestAnimationFrame(animate);
        if (!drag) {
          velX *= DAMP; velY *= DAMP;
          globeGroup.rotation.y += AUTO + velY;
          globeGroup.rotation.x = Math.max(-1.1, Math.min(1.1, globeGroup.rotation.x + velX));
        }
        const t = Date.now() * 0.001;
        ring.scale.setScalar(1 + 0.15 * Math.sin(t * 2));
        ringMat.opacity = 0.4 + 0.25 * Math.sin(t * 2);
        arcData.forEach((arc, i) => {
          arc.progress = (arc.progress + 0.0028) % 1;
          const idx = Math.floor(arc.progress * (arc.pts.length - 1));
          arc.particle.position.copy(arc.pts[idx]);
          arc.lineMat.opacity = 0.22 + 0.12 * Math.sin(t * 0.8 + i * 0.9);
        });
        if (renderer && camera) renderer.render(scene, camera);
      };
      animate();
    };

    // ResizeObserver on the wrapper div — always syncs renderer to real pixel size
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const W = Math.floor(entry.contentRect.width);
        const H = Math.floor(entry.contentRect.height);
        if (W < 10 || H < 10) continue;

        if (!renderer || !camera) {
          // First time we have real dimensions → initialize
          initScene(W, H);
        } else {
          // Already initialized → just resize
          renderer.setSize(W, H);
          camera.aspect = W / H;
          camera.updateProjectionMatrix();
        }
      }
    });
    ro.observe(wrap);

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
      if (eventsAttached && mmDown && mmMove && onUp && mtStart && mtEnd && mtMove) {
        canvas.removeEventListener('mousedown', mmDown);
        window.removeEventListener('mouseup', onUp);
        canvas.removeEventListener('mousemove', mmMove);
        canvas.removeEventListener('touchstart', mtStart);
        window.removeEventListener('touchend', mtEnd);
        canvas.removeEventListener('touchmove', mtMove);
      }
      if (renderer) renderer.dispose();
      if (earthTex) earthTex.dispose();
    };
  }, []);

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #020e1c 0%, #041525 55%, #041525 100%)' }}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pt-16 md:pt-20 pb-0">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <span
            className="inline-block text-[10px] tracking-[0.25em] uppercase font-label mb-4"
            style={{ color: '#4dd9f0' }}
          >
            Destinos ao Redor do Mundo
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-white leading-tight">
            O mundo é o seu <span className="italic" style={{ color: '#4dd9f0' }}>playground</span>
          </h2>
          <p className="mt-4 text-white/40 text-xs md:text-sm font-light max-w-sm mx-auto leading-relaxed">
            Arraste ou toque para girar · Passe o mouse nos pontos para explorar destinos
          </p>
        </div>

        {/* Globe wrapper — drives ResizeObserver */}
        <div
          ref={wrapRef}
          className="relative w-full"
          style={{ height: 'clamp(300px, 80vw, 680px)' }}
        >
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full block" />

          {/* Tooltip */}
          {tooltip && (
            <div
              className="fixed z-[60] pointer-events-none"
              style={{ left: tooltip.x + 18, top: tooltip.y, transform: 'translateY(-110%)' }}
            >
              <div
                className="rounded-xl px-4 py-3 min-w-[148px]"
                style={{
                  background: 'rgba(4,18,36,0.92)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(77,217,240,0.35)',
                }}
              >
                <div className="text-xl mb-1">{tooltip.emoji}</div>
                <div className="font-heading text-lg font-medium text-white">{tooltip.name}</div>
                <div className="text-white/55 text-xs mt-0.5">{tooltip.country}</div>
                <div className="text-xs mt-1 font-label" style={{ color: '#4dd9f0' }}>{tooltip.region}</div>
              </div>
            </div>
          )}

          {/* Fortaleza label */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 pointer-events-none">
            <div className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
            <span className="text-white/35 text-[10px] font-label tracking-widest uppercase whitespace-nowrap">
              Fortaleza · Origem
            </span>
          </div>
        </div>

        {/* Destination tags */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mt-6 md:mt-8 pb-14 md:pb-18">
          {globeDestinations.map((dest) => (
            <span
              key={dest.name}
              className="inline-flex items-center gap-1.5 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-white/55 text-xs font-light tracking-wide hover:text-white transition-colors duration-300 whitespace-nowrap cursor-default"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <span className="text-xs">{dest.emoji}</span>
              {dest.name}
            </span>
          ))}
        </div>
      </div>

      {/* Gradient fade into next section */}
      <div
        className="absolute bottom-0 left-0 right-0 h-32 md:h-40 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, transparent 0%, rgba(248,247,246,0.4) 55%, #f8f7f6 100%)',
        }}
      />
    </section>
  );
}